import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const COLORS = [
  '#1a73e8', '#d93025', '#0d652d', '#e37400', '#9334e6',
  '#00796b', '#c2185b', '#5c6bc0', '#ff6f00', '#2e7d32',
];

interface CoolingDeltaTChartProps {
  data: Record<string, number>[];
}

export const CoolingDeltaTChart: React.FC<CoolingDeltaTChartProps> = ({ data }) => {
  const { chartData, flowRates } = useMemo(() => {
    // Compute delta_t and group by flow rate
    const grouped: Record<number, { delta_t: number; thermal_output: number }[]> = {};

    for (const point of data) {
      const flow = point.volume_flow ?? point.flow_rate ?? 0;
      const inlet = point.inlet_temp ?? point.inlet_temperature ?? 0;
      const ambient = point.ambient_temp ?? point.ambient_temperature ?? 0;
      const thermal = point.thermal_output ?? 0;
      const dt = inlet - ambient;

      if (!grouped[flow]) grouped[flow] = [];
      grouped[flow].push({ delta_t: dt, thermal_output: thermal });
    }

    const flows = Object.keys(grouped).map(Number).sort((a, b) => a - b);

    // Build chart data: unique delta_t values as x, one y column per flow rate
    const dtSet = new Set<number>();
    for (const flow of flows) {
      for (const pt of grouped[flow]) {
        dtSet.add(pt.delta_t);
      }
    }
    const dts = Array.from(dtSet).sort((a, b) => a - b);

    // Build lookup for fast access
    const lookup: Record<string, number> = {};
    for (const flow of flows) {
      for (const pt of grouped[flow]) {
        lookup[`${pt.delta_t}_${flow}`] = pt.thermal_output;
      }
    }

    const cData = dts.map(dt => {
      const row: Record<string, number> = { delta_t: dt };
      for (const flow of flows) {
        const val = lookup[`${dt}_${flow}`];
        if (val !== undefined) {
          row[`flow_${flow}`] = val;
        }
      }
      return row;
    });

    return { chartData: cData, flowRates: flows };
  }, [data]);

  if (chartData.length === 0) {
    return <div style={{ fontSize: 13, color: '#9aa0a6', padding: '8px 0' }}>No data for chart</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid stroke="#f1f3f4" strokeDasharray="4 4" />
        <XAxis
          dataKey="delta_t"
          tick={{ fontSize: 10, fill: '#5f6368' }}
          label={{ value: 'dT (T_inlet - T_ambient) [K]', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#5f6368' }}
        />
        <YAxis
          tick={{ fontSize: 10, fill: '#5f6368' }}
          label={{ value: 'Thermal Output [kW]', angle: -90, position: 'insideLeft', offset: 10, fontSize: 10, fill: '#5f6368' }}
          width={55}
        />
        <Tooltip
          contentStyle={{ fontSize: 11, maxWidth: 240 }}
          formatter={(value: any, name?: string) => [
            Number(value).toFixed(2) + ' kW',
            (name || '').replace('flow_', '') + ' l/min',
          ]}
          labelFormatter={(label: any) => `dT: ${label} K`}
        />
        <Legend
          formatter={(value: string) => value.replace('flow_', '') + ' l/min'}
          wrapperStyle={{ fontSize: 10 }}
        />
        {flowRates.map((flow, i) => (
          <Line
            key={flow}
            type="monotone"
            dataKey={`flow_${flow}`}
            stroke={COLORS[i % COLORS.length]}
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 3 }}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
